import type { ReactNode } from 'react';
import { Card, Table } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import SubtleBadge from '@/components/common/SubtleBadge';
import type { ChartGuideKey } from '../../lib/chart-guides';
import { evolutionPct } from '../../lib/advanced-stats';
import { formatFaNumber } from '../../lib/utils';
import { IndexerChartCard } from '../IndexerChartCard';
import { ChartHelpTip } from './ChartHelpTip';

type ComparisonRow = {
  label: string;
  current: number;
  previous: number;
  invertEvolution?: boolean;
  href?: string;
  tone?: 'primary' | 'success' | 'warning' | 'danger' | 'secondary' | 'info';
};

function EvoBadge({
  current,
  previous,
  invert,
}: {
  current: number;
  previous: number;
  invert?: boolean;
}) {
  const pct = evolutionPct(current, previous);
  if (pct === null) {
    return (
      <SubtleBadge bg="secondary" pill className="fs--2">
        جدید
      </SubtleBadge>
    );
  }
  if (pct === 0) {
    return (
      <SubtleBadge bg="secondary" pill className="fs--2">
        ۰٪
      </SubtleBadge>
    );
  }
  const positive = pct > 0;
  const bad = invert ? positive : !positive;
  const tone = bad ? 'danger' : 'success';
  const sign = positive ? '+' : '';
  return (
    <SubtleBadge bg={tone} pill className="fs--2">
      {sign}
      {formatFaNumber(pct)}٪
    </SubtleBadge>
  );
}

export function ComparisonTable({
  title,
  subtitle,
  guideKey,
  rows,
  footer,
}: {
  title: string;
  subtitle?: string;
  guideKey?: ChartGuideKey;
  rows: ComparisonRow[];
  footer?: ReactNode;
}) {
  const navigate = useNavigate();

  return (
    <IndexerChartCard
      className="h-100 mb-0"
      title={title}
      subtitle={subtitle}
      guideKey={guideKey}
      endEl={footer}
      bodyClassName="p-0"
    >
      <Table className="comparison-table mb-0 border-top border-200" responsive hover>
        <thead className="border-bottom border-200">
          <tr>
            <th>شاخص</th>
            <th className="text-end">دورهٔ جاری</th>
            <th className="text-end">دورهٔ قبل</th>
            <th className="text-end">تغییر</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.label}
              className={row.href ? 'comparison-table-row--clickable' : undefined}
              role={row.href ? 'link' : undefined}
              tabIndex={row.href ? 0 : undefined}
              onClick={row.href ? () => navigate(row.href!) : undefined}
              onKeyDown={
                row.href
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(row.href!);
                      }
                    }
                  : undefined
              }
            >
              <td>
                <SubtleBadge bg={row.tone ?? 'secondary'} pill className="fs--2">
                  {row.label}
                </SubtleBadge>
              </td>
              <td className="text-end comparison-num">
                {formatFaNumber(row.current)}
              </td>
              <td className="text-end comparison-num text-secondary">
                {formatFaNumber(row.previous)}
              </td>
              <td className="text-end">
                <EvoBadge
                  current={row.current}
                  previous={row.previous}
                  invert={row.invertEvolution}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </IndexerChartCard>
  );
}

export function ComparisonHeroCards({
  cards,
}: {
  cards: {
    label: string;
    guideKey?: ChartGuideKey;
    current: number;
    previous: number;
    invert?: boolean;
    tone?: 'primary' | 'success' | 'warning' | 'danger';
    href?: string;
  }[];
}) {
  return (
    <div className="comparison-hero-grid">
      {cards.map((card) => {
        const pct = evolutionPct(card.current, card.previous);
        const positive = card.current - card.previous > 0;
        const bad = card.invert ? positive : !positive;
        const evoTone =
          pct === 0 || pct === null
            ? 'neutral'
            : bad
              ? 'danger'
              : 'success';
        const body = <HeroCardBody card={card} pct={pct} evoTone={evoTone} />;
        return (
          <Card
            key={card.label}
            className={`mb-0 h-100 comparison-hero-card comparison-hero-card--${card.tone ?? 'primary'}${
              card.href ? ' comparison-hero-card--clickable' : ''
            }`}
          >
            <Card.Body className="py-3 px-3">
              {card.href ? (
                <Link to={card.href} className="text-decoration-none text-reset d-block">
                  {body}
                </Link>
              ) : (
                body
              )}
            </Card.Body>
          </Card>
        );
      })}
    </div>
  );
}

function HeroCardBody({
  card,
  pct,
  evoTone,
}: {
  card: {
    label: string;
    guideKey?: ChartGuideKey;
    current: number;
    previous: number;
  };
  pct: number | null;
  evoTone: string;
}) {
  return (
    <>
      <div className="comparison-hero-label">
        {card.label}
        {card.guideKey && <ChartHelpTip guideKey={card.guideKey} />}
      </div>
      <div className="comparison-hero-values">
        <span className="comparison-hero-current">
          {formatFaNumber(card.current)}
        </span>
        <span className="comparison-hero-prev text-secondary">
          قبلی: {formatFaNumber(card.previous)}
        </span>
      </div>
      {pct !== null && (
        <div className={`comparison-hero-evo comparison-hero-evo--${evoTone}`}>
          {pct > 0 ? '+' : ''}
          {formatFaNumber(pct)}٪
        </div>
      )}
    </>
  );
}
